import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Share2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { catMap } from "@/lib/categories";
import { PointIcon, Coin, fmtDate, fmtK } from "@/lib/points";

const LIMIT = { day: "në ditë", week: "në javë", month: "në muaj" };

function Invite({ no }) {
  if (no == null) return null;
  const url = `${window.location.origin}/?ref=${no}`;
  const share = async () => {
    try { if (navigator.share) await navigator.share({ title: "GjejShqip", text: "Bashkohu me mua në GjejShqip!", url }); else { await navigator.clipboard.writeText(url); toast.success("Linku u kopjua"); } } catch { return; }
    api.post("/points/share").catch(() => {});
  };
  return (
    <button data-testid="points-invite" onClick={share} className="w-full mt-3 flex items-center gap-3 bg-emerald-500 text-white rounded-2xl p-4 font-semibold active:scale-[0.98] transition-transform">
      <Share2 className="w-5 h-5" /><span className="flex-1 text-left">Fto një shok me linkun tënd</span>
    </button>
  );
}

function Prices() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/credits/info").then((r) => setC(r.data)).catch(() => setC(false)); }, []);
  if (!c) return <p className="text-center text-sm text-slate-400 py-6">Duke ngarkuar...</p>;
  const lab = Object.fromEntries(c.features.map((f) => [f.key, f.label]));
  const cats = Object.entries(c.prices || {}).map(([cat, fs]) => [cat, Object.entries(fs).filter(([, v]) => !v.off && v.price > 0)]).filter(([, fs]) => fs.length);
  const promos = Object.entries(c.promo || {}).filter(([, v]) => v.on);
  return (
    <>
      {promos.map(([k, v]) => (
        <div key={k} className="bg-white rounded-2xl p-3 flex justify-between border border-slate-100 text-sm" data-testid={`price-promo-${k}`}><span>{c.promo_labels[k]}</span><b className="text-amber-600">{v.price > 0 ? `${fmtK(v.price)} kredite / ditë` : "Falas"}</b></div>
      ))}
      {cats.map(([cat, fs]) => (
        <div key={cat} className="bg-white rounded-2xl p-3 border border-slate-100" data-testid={`price-cat-${cat}`}>
          <div className="font-bold text-sm text-slate-900 mb-1">{catMap[cat]?.label || cat}</div>
          {fs.map(([k, v]) => <div key={k} className="flex justify-between text-xs text-slate-600 py-0.5"><span>{lab[k]}{v.days ? ` · ${v.days} ditë` : ""}</span><b className="text-amber-600">{fmtK(v.price)} kredite</b></div>)}
        </div>
      ))}
      <p className="text-[11px] text-slate-400 text-center">Çdo gjë tjetër është falas.</p>
    </>
  );
}

export default function PointsPage() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(["how", "prices"].includes(sp.get("tab")) ? sp.get("tab") : "history");
  useEffect(() => { api.get("/points/me").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === false) return <div className="p-8 text-center text-slate-500">Hyr në llogari për të parë GjejKredit.</div>;
  if (!d) return <div className="p-8 text-center text-slate-400">Duke ngarkuar...</div>;
  return (
    <div className="px-4 pt-3 pb-28" data-testid="points-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-slate-700 font-semibold mb-2" data-testid="points-back"><ArrowLeft className="w-5 h-5" />GjejKredit</button>
      <div className="rounded-3xl bg-gradient-to-br from-amber-50 to-amber-100 p-4">
        <div className="flex items-center gap-3">
          <Coin size={72} />
          <div><div className="text-3xl font-black text-slate-900" data-testid="points-total">{fmtK(d.total)} <span className="text-xl">kredite</span></div><div className="text-sm text-amber-700">Monedha jote në GjejShqip</div></div>
        </div>
        <button data-testid="points-buy" onClick={() => nav("/gjejkredit/bli")} className="w-full mt-3 h-12 rounded-2xl bg-amber-500 text-white font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><ShoppingCart className="w-5 h-5" />Bli GjejKredit</button>
      </div>
      <Invite no={d.member_no} />
      <div className="flex gap-2 mt-5">
        {[["history", "Historia"], ["how", "Si fitoj kredite?"], ["prices", "Çmimet"]].map(([k, l]) => (
          <button key={k} data-testid={`points-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 h-10 rounded-full text-xs sm:text-sm font-bold ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-3 space-y-2">
        {tab === "history" && (d.history.length ? d.history.map((h) => (
          <div key={h.id} className="bg-white rounded-2xl p-3 flex items-center gap-3 border border-slate-100" data-testid={`points-hist-${h.id}`}>
            <span className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-700"><PointIcon name={h.icon} className="w-5 h-5" /></span>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-800 break-words">{h.name}</div><div className="text-[11px] text-slate-400">{fmtDate(h.created_at)}{h.note ? ` · ${h.note}` : ""}</div></div>
            <div className={`font-extrabold whitespace-nowrap ${h.points >= 0 ? "text-emerald-600" : "text-rose-500"}`}>{h.points > 0 ? "+" : ""}{fmtK(h.points)} kredite</div>
          </div>
        )) : <p className="text-center text-sm text-slate-400 py-6" data-testid="points-empty">Ende nuk ke GjejKredit. Shiko “Si fitoj kredite?”</p>)}
        {tab === "prices" && <Prices />}
        {tab === "how" && d.activities.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl p-3 flex items-center gap-3 border border-slate-100" data-testid={`points-how-${a.key}`}>
            <span className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-800"><PointIcon name={a.icon} className="w-5 h-5" /></span>
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-800 break-words">{a.name}</div><div className="text-[11px] text-slate-500">{a.description}{a.once ? " · një herë" : a.limit_count && a.limit_period !== "none" ? ` · deri ${a.limit_count} herë ${LIMIT[a.limit_period]}` : ""}</div></div>
            <div className={`text-center rounded-xl px-2.5 py-1 ${a.points < 0 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-700"}`}><div className="font-extrabold">{a.points > 0 ? "+" : ""}{fmtK(a.points)}</div><div className="text-[10px]">kredite</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}
